import qs from "qs";

import { ROLE_OBJECT_TYPE } from "@/common/enums/roleObjectType";

export const STRINGIFY_OPTIONS: qs.IStringifyOptions = {
  arrayFormat: "indices",
  skipNulls: true,
  filter: (prefix, value) => {
    if (value === "") {
      // Return an `undefined` value to omit a property.
      return;
    }
    return value;
  },
};

const DOMAIN_NAME_REGEX =
  /^(?!www\.)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i;

export const SOCIAL_MEDIA_REGEX = {
  facebook: /^[a-z\d.-]{3,}$/i,
  twitter: /^[a-z\d_]{1,15}$/i,
  linkedin: /^(in|company|school|showcase|group)\/(.+?)/i,
  linkedin_person: /^(in)\/(.+?)/i,
  linkedin_organization: /^(company|school)\/(.+?)/i,
  youtube: /^(@|channel\/)(.+?)/i,
  instagram: /^[a-z\d._]{1,30}$/i,
  // TikTok: stored without @, 2-24 chars, letters/numbers/dots/underscores
  tiktok: /^[a-z\d._]{2,24}$/i,
  // Bluesky: domain-style handle e.g. user.bsky.social or custom-domain handle
  bluesky: /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/,
};

/**
 * To validate string is valid url.
 * @param {string} value
 */
export function isValidUrl(value: string) {
  try {
    return Boolean(new URL(value));
  } catch {
    return false;
  }
}

export function isValidDomain(value: string) {
  return DOMAIN_NAME_REGEX.test(value);
}

export function appendHttpIfNotExists(url: string) {
  if (!url) {
    return "";
  }
  if (typeof url !== "string") {
    return url;
  }
  return url.startsWith("http") ? url : `http://${url}`;
}

const ROUTE_MAP: Record<string, string> = {
  [ROLE_OBJECT_TYPE.TOUCHPOINT]: "touchpoints",
  [ROLE_OBJECT_TYPE.STAKEHOLDER]: "stakeholders",
  [ROLE_OBJECT_TYPE.STAKEHOLDER_GROUP]: "stakeholder-groups",
  [ROLE_OBJECT_TYPE.ENGAGEMENT_PROJECT]: "plans",
  [ROLE_OBJECT_TYPE.USER_GROUP]: "user-groups",
  [ROLE_OBJECT_TYPE.NETWORK]: "networks",
  [ROLE_OBJECT_TYPE.TASK]: "tasks",
} as const;

export function getRoute({
  organizationId,
  projectId,
  objectType,
  objectId,
  params,
}: {
  organizationId: number | string;
  projectId: number | string;
  objectType?: string;
  objectId?: number;
  params?: Record<string, any>;
}) {
  let route = `/organizations/${organizationId}/workspaces/${projectId}`;

  if (objectType) {
    route += `/${ROUTE_MAP[objectType]}`;
  }

  if (objectId) {
    route += `/${objectId}`;
  }

  if (params) {
    route += `?${qs.stringify(params)}`;
  }

  return route;
}

/**
 * A task's own page (GEN5-5282). The list's `tasks?taskId=` deep link still
 * opens the dialog; links that leave the list point here.
 */
export const getTaskPath = ({
  organizationId,
  projectId,
  taskId,
}: {
  organizationId: number | string;
  projectId: number | string;
  taskId: number | string;
}) =>
  `${getRoute({
    organizationId,
    projectId,
    objectType: ROLE_OBJECT_TYPE.TASK,
  })}/${taskId}`;
